import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { roleEndpoints } from '../api/endpoints.js';
import { useSoftDeleteRole } from '../api/mutations.js';
import { type RoleRow, roleRow } from '../api/schemas.js';

/**
 * The roles list.
 *
 * Search, sort, pagination and the non-data states all come from
 * `ResourceListPage`. What is here is the descriptor: which columns exist, which
 * backend fields each one reads, and what a row can be done to.
 */

function systemLabel(isSystem: boolean | undefined): string {
  return isSystem === true ? 'Yes' : 'No';
}

export const roleColumns: readonly ColumnDescriptor<RoleRow>[] = [
  {
    id: 'name',
    header: 'Role',
    sortable: true,
    alwaysVisible: true,
    fields: ['name'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/roles/${row.id}`}
        secondary={row.description ?? null}
      />
    ),
  },
  {
    id: 'description',
    header: 'Description',
    fields: ['description'],
    cell: (row) => row.description ?? '—',
  },
  {
    id: 'isSystem',
    header: 'System',
    sortable: true,
    fields: ['isSystem'],
    cell: (row) => systemLabel(row.isSystem),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: RoleRow): React.JSX.Element => <RoleRowActions row={row} />,
  },
];

function RoleRowActions({ row }: { readonly row: RoleRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteRole();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/roles/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/roles/${row.id}/edit` },
          ]}
        />
        {row.isSystem === true ? null : (
          <PermissionGuard permission={PERMISSIONS.ROLE_WRITE}>
            <RowActions
              actions={[
                {
                  label: `Delete ${label}`,
                  icon: Trash2,
                  destructive: true,
                  onClick: () => {
                    setConfirming(true);
                  },
                },
              ]}
            />
          </PermissionGuard>
        )}
      </div>
      {row.isSystem === true ? null : (
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Delete role"
          description={`${label} will be removed and every user who held it will lose it. This does not delete their account, only the role.`}
          confirmLabel="Delete"
          destructive
          loading={softDelete.isPending}
          onConfirm={() => {
            softDelete.mutate(row.id, {
              onSuccess: () => {
                setConfirming(false);
              },
            });
          }}
        />
      )}
    </>
  );
}

export function RoleListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Roles"
      description="Custom and system roles for this tenant."
      path={roleEndpoints.list}
      resource="roles"
      schema={roleRow}
      columns={roleColumns}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.ROLE_READ}
      defaultSort={{ columnId: 'createdAt', direction: 'desc' }}
      actions={
        <PermissionGuard permission={PERMISSIONS.ROLE_WRITE}>
          <Button asChild>
            <Link to="/roles/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New role
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
